import React, {useRef, useState} from 'react';
import {Box, IconButton, List, ListItem, Paper, TextField, Tooltip, Typography} from '@mui/material';
import {EmojiEmotions, Gif, Send, Star} from '@mui/icons-material';
import UserAvatar from '../UserAvatar';
import GifPickerDialog from './dialogs/GifPickerDialog';
import '../../styles/components/chat.css';

export default function ChatComponent({
  messages,
  chatMessage,
  onMessageChange,
  onMessageSubmit,
  onGifClick,
  currentUser,
  gameMasterId,
  onEmojiButtonClick
}) {
  const chatEndRef = useRef(null);
  const [isGifPickerOpen, setIsGifPickerOpen] = useState(false);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onMessageSubmit(e);
    }
  };

  const handleGifButtonClick = () => {
    setIsGifPickerOpen(true);
  };

  const handleGifPickerClose = () => {
    setIsGifPickerOpen(false);
  };

  const handleGifSelect = (gifUrl) => {
    onGifClick(gifUrl);
    setIsGifPickerOpen(false);
  };

  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial scroll to bottom
  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView();
  }, []);

  return (
    <Paper className="glass chat-container">
      <Box className="box-header">
        <Typography variant="h6">
          Chat
        </Typography>
      </Box>

      <Box className="box-divider" />

      <Box className="chat-messages">
        <List>
          {messages.map((message) => (
            <ListItem 
              key={message.id}
              className={`chat-message-item ${message.userId === currentUser.uid ? 'own-message' : ''}`}
            >
              <Box className="chat-avatar">
                <UserAvatar
                  src={message.hasRpmAvatar ? message.rpmModelUrl?.replace('.glb', '.png') : message.photoURL}
                  alt={message.displayName}
                  size={40}
                  hasRpmAvatar={Boolean(message.hasRpmAvatar)}
                  rpmModelUrl={message.rpmModelUrl}
                  rpmAvatarConfig={message.rpmAvatarConfig}
                  rpmAvatarType={message.rpmAvatarType}
                />
              </Box>
              <Box className="chat-message-content">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" component="span" sx={{ 
                    color: 'var(--color-secondary)',
                    fontWeight: 'bold',
                    fontSize: '1.0rem'
                  }}>
                    {message.displayName}
                  </Typography>
                  {message.userId === gameMasterId && (
                    <Tooltip 
                      title="Game Master"
                      placement="top"
                      classes={{
                        popper: 'tooltip-custom'
                      }}
                      PopperProps={{
                        disablePortal: true,
                        modifiers: [{
                          name: 'offset',
                          options: {
                            offset: [0, -8]
                          }
                        }]
                      }}
                    >
                      <IconButton 
                        size="small" 
                        disableRipple 
                        className="icon-button-transparent"
                      >
                        <Star className="game-master-icon" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {message.formattedTime && (
                    <Typography variant="caption" sx={{ ml: 'auto', color: 'var(--color-secondary)' }}>
                      {message.formattedTime}
                    </Typography>
                  )}
                </Box>
                {message.gifUrl ? (
                  <Box sx={{ mt: 0.5 }}>
                    <img 
                      src={message.gifUrl} 
                      alt="GIF"
                      className="chat-gif"
                      onClick={(e) => e.preventDefault()}
                    />
                  </Box>
                ) : (
                  <Typography 
                    variant="body2" 
                    component="span" 
                    className="chat-message"
                  >
                    {message.text}
                  </Typography>
                )}
              </Box>
            </ListItem>
          ))}
          <div ref={chatEndRef} style={{ float: 'left', clear: 'both' }} />
        </List>
      </Box>

      <Box component="form" onSubmit={onMessageSubmit} className="chat-input-container">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Nachricht eingeben..."
          value={chatMessage}
          onChange={onMessageChange}
          onKeyPress={handleKeyPress}
          size="small"
          multiline
          maxRows={3}
          className="chat-input"
          sx={{
            '& .MuiInputBase-root': {
              color: 'var(--color-text-primary)',
              background: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        />
        <IconButton
          color="primary"
          onClick={onEmojiButtonClick}
          className="chat-button"
        >
          <EmojiEmotions />
        </IconButton>
        <IconButton
          color="primary"
          onClick={handleGifButtonClick}
          className="chat-button"
        >
          <Gif />
        </IconButton>
        <IconButton 
          color="primary" 
          type="submit"
          disabled={!chatMessage.trim()}
          className="chat-button"
          sx={{ 
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          <Send />
        </IconButton>
      </Box>

      <GifPickerDialog
        open={isGifPickerOpen}
        onClose={handleGifPickerClose}
        onGifSelect={handleGifSelect}
      />
    </Paper>
  );
} 